"use client";

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  addCartItem,
  applyCartPoints,
  getCart,
  removeCartItem,
  updateCartItemQuantity,
} from "../api";
import type { CartPointsUsageResponseApi, NormalizedCart } from "../types";
import {
  normalizeCustomerProfile,
  profileApi,
} from "@/src/features/profile/api";
import { useAppDispatch, useAppSelector } from "@/src/store/hooks";
import { clearCartSummary, setCartSummary } from "@/src/store/slices/cartSlice";
import { saveGuestCartHandoff } from "../guestCartHandoff";

type CartMutationState =
  | {
      type: "add";
      variantId: number;
    }
  | {
      type: "update";
      itemId: number;
      quantity: number;
    }
  | {
      type: "remove";
      itemId: number;
    }
  | null;

function getCartErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : "";

  if (message.includes("Out of stock")) {
    return "الكمية المطلوبة غير متاحة في المخزون";
  }

  if (message.includes("Item not found")) {
    return "قطعة الأثاث المطلوبة غير موجودة في عربة التسوق";
  }

  if (message.includes("Authentication credentials")) {
    return "تعذر الوصول إلى عربة التسوق حاليًا";
  }

  return "حدث خطأ، حاول مرة أخرى";
}

function getPointsErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : "";

  if (message.includes("Points exceed balance")) {
    return "لا يمكنك استخدام كاش أكثر من رصيدك الحالي";
  }

  if (message.includes("Invalid points")) {
    return "أدخل قيمة كاش صالحة";
  }

  if (message.includes("No wallet")) {
    return "لا توجد محفظة كاش مرتبطة بهذا الحساب";
  }

  if (message.includes("Authentication credentials")) {
    return "سجّل دخولك أولًا لاستخدام الكاش";
  }

  return "تعذر تطبيق الكاش الآن";
}

export function useCart(options?: { autoLoad?: boolean }) {
  const autoLoad = options?.autoLoad ?? true;
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [cart, setCart] = useState<NormalizedCart | null>(null);
  const [availablePoints, setAvailablePoints] = useState<number | null>(null);
  const [currentDiscountPercentage, setCurrentDiscountPercentage] =
    useState<number | null>(null);
  const [remainingDiscountPercentage, setRemainingDiscountPercentage] =
    useState<number | null>(null);
  const [pointsPreview, setPointsPreview] =
    useState<CartPointsUsageResponseApi | null>(null);
  const [isLoading, setIsLoading] = useState(autoLoad);
  const [isMutating, setIsMutating] = useState(false);
  const [isApplyingPoints, setIsApplyingPoints] = useState(false);
  const [mutationState, setMutationState] = useState<CartMutationState>(null);
  const [error, setError] = useState<string | null>(null);

  const syncSummary = useCallback(
    (nextCart: NormalizedCart) => {
      saveGuestCartHandoff(nextCart);
      dispatch(
        setCartSummary({
          totalItems: nextCart.totalItems,
          totalPrice: nextCart.total,
          type: nextCart.type,
          variantIds: nextCart.items.map((item) => item.variantId),
        }),
      );
    },
    [dispatch],
  );

  const loadAvailablePoints = useCallback(async () => {
    if (!accessToken) {
      setAvailablePoints(null);
      return;
    }

    try {
      const profile = await profileApi.getCurrentCustomerProfile();
      const normalizedProfile = normalizeCustomerProfile(profile);
      const currentDiscount = normalizedProfile.currentDiscountPercentage;

      setAvailablePoints(profile.points);
      setCurrentDiscountPercentage(currentDiscount);
      setRemainingDiscountPercentage(null);
    } catch {
      setAvailablePoints(null);
      setCurrentDiscountPercentage(null);
      setRemainingDiscountPercentage(null);
    }
  }, [accessToken]);

  const loadCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const nextCart = await getCart(accessToken);
      setCart(nextCart);
      setPointsPreview(null);
      syncSummary(nextCart);
      setIsLoading(false);

      if (nextCart.type === "user" && accessToken) {
        void loadAvailablePoints();
      } else {
        setAvailablePoints(null);
        setCurrentDiscountPercentage(null);
        setRemainingDiscountPercentage(null);
      }
    } catch (loadError) {
      const message = getCartErrorMessage(loadError);
      setError(message);
      dispatch(clearCartSummary());
      setIsLoading(false);
    }
  }, [accessToken, dispatch, loadAvailablePoints, syncSummary]);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadCart();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [autoLoad, loadCart]);

  const addItem = useCallback(
    async (variantId: number) => {
      setIsMutating(true);
      setMutationState({ type: "add", variantId });

      try {
        await addCartItem(variantId, accessToken);
        const nextCart = await getCart(accessToken);
        setCart(nextCart);
        setPointsPreview(null);
        syncSummary(nextCart);

        if (nextCart.type === "user" && accessToken) {
          void loadAvailablePoints();
        }

        toast.success("تمت إضافة قطعة الأثاث إلى عربة التسوق");
        return true;
      } catch (mutationError) {
        toast.error(getCartErrorMessage(mutationError));
        return false;
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [accessToken, loadAvailablePoints, syncSummary],
  );


  const addItems = useCallback(
    async (variantIds: number[]) => {
      const uniqueVariantIds = Array.from(new Set(variantIds)).filter(
        (variantId) => Number.isInteger(variantId) && variantId > 0,
      );

      if (uniqueVariantIds.length === 0) {
        toast.error("اختار قطعة واحدة على الأقل");
        return;
      }

      setIsMutating(true);
      setMutationState({ type: "add", variantId: uniqueVariantIds[0] });

      try {
        const baseCart = cart ?? (await getCart(accessToken));
        const existingVariantIds = new Set(
          baseCart.items.map((item) => item.variantId),
        );
        const idsToAdd = uniqueVariantIds.filter(
          (variantId) => !existingVariantIds.has(variantId),
        );

        if (idsToAdd.length === 0) {
          setCart(baseCart);
          syncSummary(baseCart);
          toast.success("الاختيارات المحددة موجودة بالفعل في عربة التسوق");
          return true;
        }

        for (const variantId of idsToAdd) {
          await addCartItem(variantId, accessToken);
        }

        const nextCart = await getCart(accessToken);
        setCart(nextCart);
        setPointsPreview(null);
        syncSummary(nextCart);

        if (nextCart.type === "user" && accessToken) {
          void loadAvailablePoints();
        }

        toast.success(
          idsToAdd.length === 1
            ? "تمت إضافة قطعة الأثاث إلى عربة التسوق"
            : "تمت إضافة الاختيارات المحددة إلى عربة التسوق",
        );
        return true;
      } catch (mutationError) {
        toast.error(getCartErrorMessage(mutationError));
        return false;
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [accessToken, cart, loadAvailablePoints, syncSummary],
  );
  const updateItemQuantity = useCallback(
    async (itemId: number, quantity: number) => {
      setIsMutating(true);
      setMutationState({ type: "update", itemId, quantity });

      try {
        const currentCart = cart ?? (await getCart(accessToken));
        const currentItem = currentCart.items.find((item) => item.itemId === itemId);

        if (!currentItem || !Number.isInteger(quantity) || quantity < 1) {
          throw new Error("Item not found");
        }

        if (quantity > currentItem.stock) {
          throw new Error("Out of stock");
        }

        if (currentCart.type === "guest") {
          if (quantity > currentItem.quantity) {
            for (let index = currentItem.quantity; index < quantity; index += 1) {
              await addCartItem(currentItem.variantId, accessToken);
            }
          } else if (quantity < currentItem.quantity) {
            await removeCartItem(currentItem.itemId, accessToken, currentItem.variantId);

            for (let index = 0; index < quantity; index += 1) {
              await addCartItem(currentItem.variantId, accessToken);
            }
          }
        } else {
          await updateCartItemQuantity(itemId, quantity, accessToken);
        }

        const nextCart = await getCart(accessToken);
        const syncedItem = nextCart.items.find((item) => item.variantId === currentItem.variantId);

        if (!syncedItem || syncedItem.quantity !== quantity) {
          throw new Error("Out of stock");
        }

        setCart(nextCart);
        setPointsPreview(null);
        syncSummary(nextCart);

        if (nextCart.type === "user" && accessToken) {
          void loadAvailablePoints();
        }

        toast.success("تم تحديث كمية قطعة الأثاث");
      } catch (mutationError) {
        toast.error(getCartErrorMessage(mutationError));
        return false;
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [accessToken, cart, loadAvailablePoints, syncSummary],
  );

  const removeItem = useCallback(
    async (itemId: number, variantId?: number) => {
      setIsMutating(true);
      setMutationState({ type: "remove", itemId });

      try {
        await removeCartItem(itemId, accessToken, variantId);
        const nextCart = await getCart(accessToken);
        setCart(nextCart);
        setPointsPreview(null);
        syncSummary(nextCart);

        if (nextCart.type === "user" && accessToken) {
          void loadAvailablePoints();
        }

        toast.success("تم حذف قطعة الأثاث من عربة التسوق");
      } catch (mutationError) {
        toast.error(getCartErrorMessage(mutationError));
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [accessToken, loadAvailablePoints, syncSummary],
  );

  const applyPoints = useCallback(
    async (points: number) => {
      if (!Number.isInteger(points) || points < 0) {
        toast.error("أدخل قيمة كاش صالحة");
        return;
      }

      if (availablePoints !== null && points > availablePoints) {
        toast.error("لا يمكنك استخدام كاش أكثر من رصيدك الحالي");
        return;
      }

      setIsApplyingPoints(true);

      try {
        const nextPointsPreview = await applyCartPoints(points, accessToken);
        setPointsPreview(nextPointsPreview);
        toast.success("تم تحديث خصم الكاش");
      } catch (pointsError) {
        toast.error(getPointsErrorMessage(pointsError));
      } finally {
        setIsApplyingPoints(false);
      }
    },
    [accessToken, availablePoints],
  );

  const clearPointsPreview = useCallback(() => {
    setPointsPreview(null);
  }, []);

  const toggleItemByVariant = useCallback(
    async (variantId: number) => {
      setIsMutating(true);
      setError(null);

      try {
        const baseCart = cart ?? (await getCart(accessToken));
        const existingItem = baseCart.items.find(
          (item) => item.variantId === variantId,
        );

        if (existingItem) {
          setMutationState({ type: "remove", itemId: existingItem.itemId });
          await removeCartItem(existingItem.itemId, accessToken, existingItem.variantId);
        } else {
          setMutationState({ type: "add", variantId });
          await addCartItem(variantId, accessToken);
        }

        const nextCart = await getCart(accessToken);
        setCart(nextCart);
        setPointsPreview(null);
        syncSummary(nextCart);

        if (nextCart.type === "user" && accessToken) {
          void loadAvailablePoints();
        }

        toast.success(
          existingItem
            ? "تم حذف قطعة الأثاث من عربة التسوق"
            : "تمت إضافة قطعة الأثاث إلى عربة التسوق",
        );
        return !existingItem;
      } catch (mutationError) {
        toast.error(getCartErrorMessage(mutationError));
        return false;
      } finally {
        setIsMutating(false);
        setMutationState(null);
      }
    },
    [accessToken, cart, loadAvailablePoints, syncSummary],
  );

  return {
    cart,
    availablePoints,
    currentDiscountPercentage,
    remainingDiscountPercentage,
    pointsPreview,
    isLoading,
    isMutating,
    isApplyingPoints,
    mutationState,
    error,
    reloadCart: loadCart,
    addItem,
    addItems,
    toggleItemByVariant,
    updateItemQuantity,
    removeItem,
    applyPoints,
    clearPointsPreview,
  };
}
